import { CircularProgress, useTheme, Box } from "@mui/material";
import type React from "react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { tokens } from "../../../theme";
import { UploadedFiles, VenueFormData } from "../../../types/venue";
import { StepItem } from "../../../types/steps";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import apiService from "../../../service/Api";
import { logout } from "../../../store/slice/AuthSlice";
import { PERMISSIONS } from "../../../constants/permissions";
import { checkPermission } from "../../../service/permissionGuard";
import VenueEssentials from "./steps/VenueEssentials";
import Power from "./steps/Power";
import Internet from "./steps/InternetDetails";
import PASystems from "./steps/PAsystems";
import Rooms from "./steps/Rooms";
import Contacts from "./steps/Contacts";
import AddVenueLayout from "./AddVenueLayout";

// ── Keep all your existing interfaces ────────────────────────────────────────
interface FileUrls {
  venue: string[];
  paSystem: string[];
  equipment: string[];
  floorPlan: string[];
}

interface VenueDocument {
  id: number;
  file: string;
  file_type: string;
  venue: number;
}

interface ApiVenueResponse {
  id: number;
  name: string;
  notes: string;
  completeness_score?: number;
  venue_type: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  capacity: string;
  layouts: {
    id: number;
    layout_type: string;
    capacity: string;
    created_at: string;
    updated_at: string;
    venue: number;
  }[];
  contacts: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    twitter_handle: string | null;
    instagram_handle: string | null;
    facebook_handle: string | null;
    created_at: string;
    updated_at: string;
    venue: number;
  }[];
  files: { id: number; file: string; file_type: string; venue: number }[];
  has_power: boolean;
  power_type: string;
  power_outage_rate: string;
  power_backup: string;
  backup_power_capacity: string;
  power_distance_from_livestream_desk: string | null;
  internet_service_provider: string | null;
  wifi_name: string;
  wifi_password: string;
  internet_upload_speed: string;
  router_accessibility: string;
  router_distance_from_livestream: string | null;
  has_pa_system: boolean;
  power_socket_type: string;
  pa_system_distance_from_livestream: string;
  pa_system_provider: string;
  other_pa_system_service_provider: string;
  pa_system_contact_name: string;
  pa_system_contact_phone: string;
  pa_system_contact_email: string;
  created_at: string;
  updated_at: string;
}

const EditVenue: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams<{ id: string }>();
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileUrls, setFileUrls] = useState<FileUrls>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });
  const [remainingFileUrls, setRemainingFileUrls] = useState<FileUrls>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });
  const [venueDocuments, setVenueDocuments] = useState<VenueDocument[]>([]);

  const [formData, setFormData] = useState<VenueFormData>({
    venueName: "",
    venueType: "",
    addressOne: "",
    addressTwo: "",
    suburb: "",
    city: "",
    capacity: "",
    rooms: [],

    layouts: [{ layoutType: "", capacity: "" }],
    contact_id: undefined,
    contacts_ids: [],
    contactPersonName: "",
    contactPersonSurname: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    doesVenueHavePower: "",
    notes: "",
    powerType: "",
    powerOutageRate: "",
    powerBackup: "",
    backupPowerCapacity: "",
    router_distance_from_livestream: "",
    powerSocketType: "",
    internet_service_provider: "",
    wifi_name: "",
    wifi_password: "",
    internet_upload_speed: "",
    router_accessibility: "",
    has_pa_system: "",
    pa_system_provider: "",
    pa_system_distance_from_livestream: "",
    pa_system_contact_name: "",
    other_pa_system_service_provider: "",
    pa_system_contact_phone: "",
    pa_system_contact_email: "",
    power_distance_from_livestream_desk: "",
    uploadedFiles: { venue: [], paSystem: [], equipment: [], floorPlan: [] },
  });

  const [uploadedFiles] = useState<UploadedFiles>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });

  const steps: StepItem[] = [
    { label: "Venue Essentials" },
    { label: "Power" },
    { label: "Internet Details" },
    { label: "PA Systems" },
    { label: "Rooms" },
    { label: "Contacts" },
  ];

  // ── Keep your existing mapApiResponseToFormData exactly as-is ─────────────
  const mapApiResponseToFormData = async (
    apiData: ApiVenueResponse,
  ): Promise<VenueFormData> => {
    const contact = apiData.contacts[0] || null;

    if (!contact) {
      console.warn(`No contacts found for venue ID ${apiData.id}.`);
    }

    const venueDocuments = apiData.files
      .filter((img) => img.file_type === "venue_photos")
      .map((img) => img.file);
    const paSystemFiles = apiData.files
      .filter((img) => img.file_type === "pa_system")
      .map((img) => img.file);
    const equipmentFiles = apiData.files
      .filter((img) => img.file_type === "equipment")
      .map((img) => img.file);
    const floorPlanFiles = apiData.files
      .filter((img) => img.file_type === "floor_plan")
      .map((img) => img.file);

    setFileUrls({
      venue: venueDocuments,
      paSystem: paSystemFiles,
      equipment: equipmentFiles,
      floorPlan: floorPlanFiles,
    });
    setRemainingFileUrls({
      venue: venueDocuments,
      paSystem: paSystemFiles,
      equipment: equipmentFiles,
      floorPlan: floorPlanFiles,
    });
    setVenueDocuments(apiData.files);

    return {
      venueName: apiData.name?.trim() || "",
      venueType: apiData.venue_type?.trim() || "",
      addressOne: apiData.address_one?.trim() || "",
      addressTwo: apiData.address_two?.trim() || "",
      suburb: apiData.suburb?.trim() || "",
      completeness_score: apiData.completeness_score,
      city: apiData.city?.trim() || "",
      notes: apiData.notes?.trim() || "",
      capacity: apiData.capacity?.trim() || "",
      layouts: apiData.layouts?.map((layout) => ({
        layoutType: layout.layout_type?.trim() || "",
        capacity: layout.capacity?.trim() || "",
      })) || [{ layoutType: "", capacity: "" }],
      contact_id: contact?.id ?? undefined,
      contacts_ids: apiData.contacts.map((c) => c.id) || [],
      contactPersonName: contact?.first_name?.trim() || "",
      contactPersonSurname: contact?.last_name?.trim() || "",
      contactPersonEmail: contact?.email?.trim() || "",
      contactPersonPhone: contact?.phone?.trim() || "",
      doesVenueHavePower: apiData.has_power ? "yes" : "no",
      powerType: apiData.power_type?.trim() || "",
      powerOutageRate: apiData.power_outage_rate?.trim() || "",
      powerBackup: apiData.power_backup?.trim() || "",
      backupPowerCapacity: apiData.backup_power_capacity?.trim() || "",
      router_distance_from_livestream:
        apiData.router_distance_from_livestream?.trim() || "",
      powerSocketType: apiData.power_socket_type?.trim() || "",
      internet_service_provider:
        apiData.internet_service_provider?.trim() || "",
      wifi_name: apiData.wifi_name?.trim() || "",
      wifi_password: apiData.wifi_password?.trim() || "",
      internet_upload_speed: apiData.internet_upload_speed || "",
      router_accessibility: apiData.router_accessibility?.trim() || "",
      has_pa_system: apiData.has_pa_system ? "yes" : "no",
      pa_system_provider: apiData.pa_system_provider?.trim() || "",
      pa_system_distance_from_livestream:
        apiData.pa_system_distance_from_livestream?.trim() || "",
      pa_system_contact_name: apiData.pa_system_contact_name?.trim() || "",
      other_pa_system_service_provider:
        apiData.other_pa_system_service_provider?.trim() || "",
      pa_system_contact_phone: apiData.pa_system_contact_phone?.trim() || "",
      pa_system_contact_email: apiData.pa_system_contact_email?.trim() || "",
      power_distance_from_livestream_desk:
        apiData.power_distance_from_livestream_desk?.trim() || "",
      rooms: [], // rooms loaded separately if needed

      uploadedFiles: { venue: [], paSystem: [], equipment: [], floorPlan: [] },
    };
  };

  // ── Keep your existing useEffect/fetch exactly as-is ──────────────────────
  useEffect(() => {
    const fetchVenueData = async () => {
      if (!id) {
        showErrorToast("No venue ID provided");
        return;
      }
      setIsSubmitting(true);
      try {
        const response = await apiService.get<ApiVenueResponse>(
          `/api/venues/venue/${id}/`,
        );
        const venueData = await mapApiResponseToFormData(response.data);
        setFormData(venueData);
      } catch (err) {
        console.error("Failed to fetch venue data:", err);
        let errorMessage = "Failed to load venue data. Please try again later.";
        if (
          (err as { response?: { status?: number } })?.response?.status === 401
        ) {
          errorMessage = "Unauthorized. Please log in again.";
          dispatch(logout());
          navigate("/login");
        }
        showErrorToast(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    };
    fetchVenueData();
  }, [id, dispatch, navigate]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleInputChange = (
    fieldOrEvent:
      | keyof VenueFormData
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
    value?: string,
  ) => {
    if (typeof fieldOrEvent === "string") {
      setFormData((prev) => ({ ...prev, [fieldOrEvent]: value }));
    } else {
      const { name, value: val } = fieldOrEvent.target;
      setFormData((prev) => ({ ...prev, [name]: val }));
    }
  };

  const handleFilesChange =
    (category: keyof VenueFormData["uploadedFiles"]) =>
    async (files: File[]) => {
      const validFiles = files.filter(
        (file) => file instanceof File && file.size > 0 && file.name,
      );
      setFormData((prev) => ({
        ...prev,
        uploadedFiles: {
          ...prev.uploadedFiles,
          [category]: [...(prev.uploadedFiles[category] ?? []), ...validFiles],
        },
      }));
    };

  const handleNext = () => {
    if (activeStep < steps.length - 1) setActiveStep((p) => p + 1);
  };
  const handlePrevious = () => {
    if (activeStep > 0) setActiveStep((p) => p - 1);
  };

  // ── Keep your existing handleSubmit (PUT) exactly as-is ───────────────────
  const handleSubmit = async () => {
    checkPermission(PERMISSIONS.VENUE_CHANGE);
    if (!id) {
      showErrorToast("No venue ID provided");
      return;
    }
    setIsSubmitting(true);
    try {
      const categoryMap: Record<keyof FileUrls, string> = {
        venue: "venue_photos",
        paSystem: "pa_system",
        equipment: "equipment",
        floorPlan: "floor_plan",
      };

      const deletePromises: Promise<unknown>[] = [];
      for (const cat in categoryMap) {
        const category = cat as keyof FileUrls;
        const backendType = categoryMap[category];
        const deletedUrls = fileUrls[category].filter(
          (url) => !remainingFileUrls[category].includes(url),
        );
        for (const delUrl of deletedUrls) {
          const doc = venueDocuments.find(
            (d) => d.file === delUrl && d.file_type === backendType,
          );
          if (doc)
            deletePromises.push(
              apiService.delete(`/api/venues/document/${doc.id}/`),
            );
        }
      }
      await Promise.all(deletePromises);

      const formDataToSend = new FormData();
      const validLayouts = formData.layouts.filter(
        (l) => l.layoutType.trim() !== "",
      );

      const payload = {
        name: formData.venueName,
        venue_type: formData.venueType,
        address_one: formData.addressOne,
        address_two: formData.addressTwo,
        suburb: formData.suburb,
        city: formData.city,
        capacity: formData.capacity,
        layouts: validLayouts,
        contact_id: formData.contact_id,
        contacts_ids: [
          ...(Array.isArray(formData.contacts_ids)
            ? formData.contacts_ids
            : []),
          ...(formData.contact_id ? [Number(formData.contact_id)] : []),
        ],
        contact_person_name: formData.contactPersonName,
        contact_person_surname: formData.contactPersonSurname,
        contact_person_email: formData.contactPersonEmail,
        contact_person_phone: formData.contactPersonPhone,
        notes: formData.notes,
        has_power: formData.doesVenueHavePower === "yes",
        power_type: formData.powerType || " ",
        power_outage_rate: formData.powerOutageRate || " ",
        power_backup: formData.powerBackup || " ",
        backup_power_capacity: formData.backupPowerCapacity || " ",
        router_distance_from_livestream:
          formData.router_distance_from_livestream
            ? parseInt(formData.router_distance_from_livestream)
            : null,
        power_socket_type: formData.powerSocketType || " ",
        internet_service_provider: formData.internet_service_provider,
        wifi_name: formData.wifi_name,
        wifi_password: formData.wifi_password,
        internet_upload_speed: formData.internet_upload_speed,
        router_accessibility: formData.router_accessibility,
        has_pa_system: formData.has_pa_system === "yes",
        pa_system_provider: formData.pa_system_provider,
        pa_system_distance_from_livestream:
          formData.pa_system_distance_from_livestream
            ? parseInt(formData.pa_system_distance_from_livestream)
            : null,
        pa_system_contact_name: formData.pa_system_contact_name,
        other_pa_system_service_provider:
          formData.other_pa_system_service_provider,
        pa_system_contact_phone: formData.pa_system_contact_phone,
        pa_system_contact_email: formData.pa_system_contact_email,
        power_distance_from_livestream_desk:
          formData.power_distance_from_livestream_desk
            ? parseInt(formData.power_distance_from_livestream_desk)
            : " ",
        show_rooms: (formData.rooms ?? []).length > 0,
        rooms: (formData.rooms ?? []).map((room) => ({
          name: room.name,
          capacity: room.capacity,
          room_type: room.room_type,
          layouts: room.layouts
            .filter((l) => Number(l.capacity) > 0)
            .map((l) => ({ layout_type: l.id, capacity: l.capacity })),
        })),
      };

      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );

      formDataToSend.append("data", JSON.stringify(filteredPayload));
      console.log(
        "rooms before submit:",
        formData.rooms?.map((r) => ({
          name: r.name,
          photos: r.photos,
          floorplans: r.floorplans,
        })),
      );

      // Room files
      (formData.rooms ?? []).forEach((room, roomIndex) => {
        room.photos.forEach((file, fileIndex) => {
          if (file instanceof File && file.size > 0)
            formDataToSend.append(
              `rooms[${roomIndex}][photos][${fileIndex}]`,
              file,
            );
        });
        room.floorplans.forEach((file, fileIndex) => {
          if (file instanceof File && file.size > 0)
            formDataToSend.append(
              `rooms[${roomIndex}][floorplans][${fileIndex}]`,
              file,
            );
        });
      });

      // Venue-level files (existing logic unchanged)
      formData.uploadedFiles.venue.forEach((file, index) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          formDataToSend.append(`venue_photos[${index}]`, file);
      });
      formData.uploadedFiles.paSystem.forEach((file, index) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          formDataToSend.append(`pa_system_photos[${index}]`, file);
      });
      formData.uploadedFiles.equipment.forEach((file, index) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          formDataToSend.append(`equipment_photos[${index}]`, file);
      });
      formData.uploadedFiles.floorPlan.forEach((file, index) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          (file.type.startsWith("image/") || file.type === "application/pdf")
        )
          formDataToSend.append(`floor_plan_photos[${index}]`, file);
      });

      await apiService.put(
        `/api/venues/venue/${id}/full-update/`,
        formDataToSend,
        false,
      );

      showSuccessToast("Venue updated successfully!");
      setTimeout(() => navigate(`/venues/view/${id}`), 2000);
    } catch (error) {
      const errorResponse = (
        error as {
          response?: {
            status?: number;
            data?: { detail?: string; errors?: Record<string, unknown> };
          };
        }
      )?.response;
      let errorMessage = "Failed to update venue. Please try again.";
      if (errorResponse?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
        if (errorResponse.data.errors) {
          errorMessage +=
            ": " +
            Object.entries(errorResponse.data.errors)
              .map(([field, errors]) => {
                if (Array.isArray(errors))
                  return `${field}: ${errors.join(", ")}`;
                if (typeof errors === "object" && errors !== null)
                  return `${field}: ${Object.entries(errors)
                    .map(
                      ([i, m]) =>
                        `File ${i}: ${Array.isArray(m) ? m.join(", ") : m}`,
                    )
                    .join("; ")}`;
                return `${field}: ${JSON.stringify(errors)}`;
              })
              .join("; ");
        }
      } else if (errorResponse?.data) {
        errorMessage = JSON.stringify(errorResponse.data);
      }
      showErrorToast(errorMessage);
      console.error("Error updating venue:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Same step rendering as AddVenue ───────────────────────────────────────
  const renderStepContent = () => {
    if (isSubmitting && activeStep === 0) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress sx={{ color: colors.primary[400] }} />
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <VenueEssentials
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
            uploadedFiles={uploadedFiles}
            handleFilesChange={handleFilesChange}
          />
        );
      case 1:
        return (
          <Power
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <Internet
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );
      case 3:
        return (
          <PASystems
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );
      case 4:
        return (
          <Rooms
            rooms={formData.rooms ?? []}
            onRoomsChange={(rooms) =>
              setFormData((prev) => ({ ...prev, rooms }))
            }
          />
        );
      case 5:
        return (
          <Contacts
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
            editingContactId={editingContactId}
            setEditingContactId={setEditingContactId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AddVenueLayout
      steps={steps.map((s) => s.label)}
      activeStep={activeStep}
      formData={formData}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSubmit={handleSubmit}
      onStepClick={setActiveStep}
      isSubmitting={isSubmitting}
      setFormData={setFormData}
      onStepChange={setActiveStep}
      editingContactId={editingContactId}
      setEditingContactId={setEditingContactId}
      onRoomsChange={(rooms) => setFormData((prev) => ({ ...prev, rooms }))}
    >
      {renderStepContent()}
    </AddVenueLayout>
  );
};

export default EditVenue;
